import { useContext, useMemo } from 'react';
import type { User } from '@supabase/supabase-js';
import { AuthContext, type AuthContextValue } from '../context/AuthContext';
import { formatDate } from '../utils/date-time';

type AuthUser = User & {
  getId: () => string;
  getUserTypeLabel: () => string;
  getCreatedAtFormatted: () => string;
  isAnonymous: () => boolean;
  isPendingVerification: () => boolean;
  getPendingEmail: () => string | null;
  getEmail: () => string | null;
  getFullName: () => string | null;
  getTelephone: () => string | null;
};

type AuthContextWithUserHelpers = Omit<AuthContextValue, 'user'> & {
  user: AuthUser | null;
};

/**
 * Hook to access authentication state, methods, and helpers.
 * Must be used within an AuthProvider.
 *
 * @returns The authentication context value containing user, session, auth methods, and helpers.
 * @throws Error if used outside of AuthProvider.
 */
export function useAuth(): AuthContextWithUserHelpers {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  const userWithHelpers = useMemo<AuthUser | null>(() => {
    const { user } = context;

    if (!user) {
      return null;
    }

    return {
      ...user,
      getId: () => user.id,
      getUserTypeLabel: () => (user.is_anonymous ? 'Anoniem' : 'Geregistreerd'),
      getCreatedAtFormatted: () => (user.created_at ? formatDate(user.created_at, 'd MMMM yyyy') : 'onbekend'),
      isAnonymous: () => !!user.is_anonymous,
      isPendingVerification: () => !!user.is_anonymous && !!user.new_email,
      getPendingEmail: () => user.new_email ?? null,
      getEmail: () => user.email ?? null,
      getFullName: () => (user.user_metadata?.full_name as string) ?? null,
      getTelephone: () => (user.user_metadata?.telephone as string) ?? null,
    };
  }, [context.user]);

  return { ...context, user: userWithHelpers };
}

/** Convenience hook — returns `true` when there is no user or the user is anonymous. */
export function useAuthIsAnonymous(): boolean {
  const { user } = useAuth();
  return user?.isAnonymous() ?? true;
}

/** Convenience hook — returns `true` when the user is authenticated and not anonymous. */
export function useAuthIsVerified(): boolean {
  return !useAuthIsAnonymous();
}
